import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Shield, HeartCrack } from "lucide-react";

// AgeGate is a UX gate ONLY — server-side enforcement lives in /api/auth/register.
// sessionStorage is used (not localStorage) to reduce XSS-persistence surface;
// legitimate returning visitors will re-confirm once per browser session.
const STORAGE_KEY = "hm_age_confirmed";

export const isAgeConfirmed = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const setAgeConfirmed = () => {
  try { sessionStorage.setItem(STORAGE_KEY, "true"); } catch {}
};

/**
 * AgeGate — shown inside the sign-in dialog before revealing auth options.
 * Renders nothing (returns null via onConfirm/onDeny handled by parent) when confirmed.
 */
export default function AgeGate({ onConfirm, onDeny }) {
  const [denied, setDenied] = useState(false);
  const { t } = useTranslation();

  if (denied) {
    return (
      <div className="text-center py-6" data-testid="age-gate-denied">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#F9F5EE] flex items-center justify-center mb-3">
          <HeartCrack className="w-7 h-7 text-[#6E655F]" />
        </div>
        <h3 className="font-serif text-xl font-semibold text-[#2C2623] mb-2">
          {t("age_gate.denied_title")}
        </h3>
        <p className="text-sm text-[#6E655F] leading-relaxed max-w-xs mx-auto">
          {t("age_gate.denied_body")}
        </p>
        <Button
          onClick={onDeny}
          data-testid="age-gate-leave-btn"
          className="mt-5 rounded-full bg-[#2A524A] hover:bg-[#1F3E37]">
          {t("age_gate.leave")}
        </Button>
      </div>
    );
  }

  return (
    <div className="text-center py-2" data-testid="age-gate">
      <div className="w-14 h-14 mx-auto rounded-full bg-[#FFF0EC] flex items-center justify-center mb-3">
        <Shield className="w-7 h-7 text-[#D95D39]" />
      </div>
      <h3 className="font-serif text-2xl font-semibold text-[#2C2623] mb-2">
        {t("age_gate.question")}
      </h3>
      <p className="text-sm text-[#6E655F] leading-relaxed max-w-sm mx-auto mb-6">
        {t("age_gate.body")}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={() => { setAgeConfirmed(); onConfirm(); }}
          data-testid="age-gate-confirm-btn"
          className="h-11 px-6 rounded-full bg-[#D95D39] hover:bg-[#C24C29] text-white font-semibold">
          {t("age_gate.confirm")}
        </Button>
        <Button
          variant="outline"
          onClick={() => setDenied(true)}
          data-testid="age-gate-deny-btn"
          className="h-11 px-6 rounded-full border-[#E8DEC8] hover:bg-[#F9F5EE] text-[#2C2623]">
          {t("age_gate.deny")}
        </Button>
      </div>
      <p className="text-xs text-[#6E655F] mt-5">
        {t("age_gate.disclaimer")}
      </p>
    </div>
  );
}
